/**
 * Fire the startup API call as early as possible (before React finishes booting)
 * so ActivityList / Navbar can reuse the result instead of making a second trip.
 *
 * Uses native fetch (not axios) to stay out of the critical path / avoid circular imports.
 * Phase 1: GET /api/activities
 * Phase 2: GET /api/bootstrap (activities + wallet + notifications in one round trip)
 * On 401: refresh access token once, then retry so returning users still get the fast path.
 */

import { refreshAccessTokenOnce } from './tokenRefresh';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

let earlyPromise = null;
let activitiesConsumed = false;
let walletConsumed = false;
let notifConsumed = false;
let useBootstrap = true; // Phase 2 default; falls back to /api/activities on 404

function authHeaders() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    return {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    };
  } catch (_) {
    return null;
  }
}

async function fetchJson(path, headers) {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'GET',
    headers,
    credentials: 'omit',
  });
  if (!res.ok) {
    const err = new Error(`Early fetch failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Fetch a path; on 401 refresh the access token once and retry with the new token.
 */
async function fetchJsonWithAuthRetry(path) {
  let headers = authHeaders();
  if (!headers) {
    const err = new Error('Early fetch failed: missing token');
    err.status = 401;
    throw err;
  }

  try {
    return await fetchJson(path, headers);
  } catch (err) {
    if (err.status !== 401) throw err;

    // Expired access token — refresh once (shared with axios) then retry.
    await refreshAccessTokenOnce();
    headers = authHeaders();
    if (!headers) throw err;
    return fetchJson(path, headers);
  }
}

/**
 * Kick off the early fetch. Safe to call multiple times — only the first call fires.
 * Call from index.js before render when a token is present.
 */
export function startEarlyFetch() {
  if (earlyPromise) return earlyPromise;
  const headers = authHeaders();
  if (!headers) return null;

  earlyPromise = (async () => {
    try {
      if (useBootstrap) {
        try {
          const data = await fetchJsonWithAuthRetry('/api/bootstrap');
          return {
            activities: data?.activities || null,
            wallet: data?.wallet || null,
            notifications: data?.notifications || null,
          };
        } catch (err) {
          // Bootstrap not deployed yet / 404 → fall back to activities-only.
          if (err.status !== 404 && err.status !== 405) throw err;
          useBootstrap = false;
        }
      }
      const activities = await fetchJsonWithAuthRetry('/api/activities');
      return { activities, wallet: null, notifications: null };
    } catch (err) {
      console.warn('Early fetch failed:', err?.message);
      return null;
    }
  })();

  return earlyPromise;
}

/**
 * One-shot: returns the activities payload (paginated or array) once, then null.
 * Callers fall back to their normal getActivities() on null.
 */
export async function consumeEarlyActivities() {
  if (activitiesConsumed) return null;
  activitiesConsumed = true;
  if (!earlyPromise) startEarlyFetch();
  if (!earlyPromise) return null;
  try {
    const result = await earlyPromise;
    return result?.activities || null;
  } catch (_) {
    return null;
  }
}

/**
 * One-shot: returns { total_balance, group_balances } once, then null.
 */
export async function consumeEarlyWallet() {
  if (walletConsumed) return null;
  walletConsumed = true;
  if (!earlyPromise) return null;
  try {
    const result = await earlyPromise;
    return result?.wallet || null;
  } catch (_) {
    return null;
  }
}

/**
 * One-shot: returns { unread } once, then null.
 */
export async function consumeEarlyNotifications() {
  if (notifConsumed) return null;
  notifConsumed = true;
  if (!earlyPromise) return null;
  try {
    const result = await earlyPromise;
    return result?.notifications || null;
  } catch (_) {
    return null;
  }
}
